import { useState, useRef } from "react";
import {
  Upload,
  X,
  Shield,
  Lock,
  FileCheck2,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import axios from "axios";
import Reveal from "./Reveal";
import { tc, tb, tbr } from "../lib/themeStyles";

const MAIL_URL = "https://malunjkar.com/mail.php";
const COUNTRIES = [
  "United States", "Canada", "Germany", "United Kingdom", "France", "UAE",
  "Saudi Arabia", "India", "China", "Japan", "South Korea", "Australia",
  "Brazil", "Singapore", "Malaysia", "Other",
];
const INDUSTRIES = [
  "Automotive", "Oil & Gas", "Aerospace", "Heavy Equipment", "General Engineering", "Other",
];
const TIMELINES = ["Immediate", "1–3 Months", "3–6 Months", "6–12 Months", "Ongoing"];
const MATERIALS = [
  "Carbon Steel (IS 2062, ASTM A36)", "Mild Steel", "Stainless Steel 304",
  "Stainless Steel 316", "Duplex Stainless 2205", "Aluminum 6061-T6",
  "Aluminum 7075", "Copper (C11000)", "Brass (C26000)", "Specialized alloy",
];
const PROCESSES = [
  "Sheet Metal Cutting", "CNC Bending", "Precision Machining",
  "MIG Welding", "TIG Welding", "Sub-Assembly", "Full Assembly",
];
const VOLUMES = ["Prototype", "< 100 pcs", "100–500 pcs", "500–2000 pcs", "2000+ pcs"];
const ACCEPTED_EXT = [".STEP", ".IGES", ".DXF", ".DWG", ".PDF", ".SLDPRT"];
const STEP_LABELS = ["Company Profile", "Part Details", "Review & Submit"];

const initial = {
  company_name: "",
  country: "",
  industry: "",
  timeline: "",
  material: "",
  processes: [],
  annual_volume: "",
  notes: "",
  nda_requested: false,
};

export default function RFQForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleProcess = (p) =>
    setData((d) => ({
      ...d,
      processes: d.processes.includes(p) ? d.processes.filter((x) => x !== p) : [...d.processes, p],
    }));
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files || [])]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const validStep1 = data.company_name && data.country && data.industry && data.timeline;
  const validStep2 = data.material && data.processes.length > 0 && data.annual_volume;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("company_name", data.company_name);
      fd.append("country", data.country);
      fd.append("industry", data.industry);
      fd.append("timeline", data.timeline);
      fd.append("material", data.material);
      fd.append("processes", JSON.stringify(data.processes));
      fd.append("annual_volume", data.annual_volume);
      fd.append("notes", data.notes);
      fd.append("nda_requested", String(data.nda_requested));
      await axios.post(MAIL_URL, fd);
      setSuccess(true);
    } catch {
      setError("Submission failed. Please email us directly at rfq@malunjkar.com");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setData(initial);
    setFiles([]);
    setSuccess(null);
    setError("");
  };

  const progress = success ? 100 : Math.round(((step - 1) / 3) * 100 + 8);

  return (
    <section id="rfq" className="section-shell">
      <div className="section-inner">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="label-cap mb-4" style={tc.accent}>05 — Submit RFQ</div>
            <h2 className="section-title">
              Send drawings.
              <br />
              Receive a quote in 24 hrs.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.7] max-w-md" style={tc.muted}>
              Secure, encrypted upload for CAD files. Our engineering team will respond within one
              business day.
            </p>
            <div className="mt-10 space-y-4">
              {[
                { icon: Lock, label: "256-bit Encrypted Transfer" },
                { icon: Shield, label: "Files Not Shared With Third Parties" },
                { icon: FileCheck2, label: "NDA Available On Request" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3 text-[13px]" style={tc.muted}>
                  <Icon size={16} className="shrink-0" style={tc.accent} />
                  {label}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={100} className="lg:col-span-8">
            <div className="rfq-panel">
              <div className="px-6 md:px-10 pt-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="label-cap" style={tc.muted}>
                    Step <span style={tc.accent}>{success ? 3 : step}</span> of 3
                  </div>
                  <div className="label-cap" style={tc.muted}>
                    {success ? "Submitted" : STEP_LABELS[step - 1]}
                  </div>
                </div>
                <div className="h-1 w-full rounded-full overflow-hidden" style={tb.surfaceHover}>
                  <div
                    className="h-full transition-all duration-500 rounded-full"
                    style={{ ...tb.accent, width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="p-6 md:p-10">
                {success ? (
                  <div className="text-center py-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border mb-6" style={{ ...tbr.accent, ...tc.accent }}>
                      <CheckCircle2 size={32} />
                    </div>
                    <h3 className="text-[28px] font-bold tracking-[-0.02em]" style={tc.primary}>
                      RFQ Submitted Successfully.
                    </h3>
                    <p className="mt-3 max-w-md mx-auto" style={tc.muted}>
                      We will contact you within 24 hours.
                    </p>
                    <div className="mt-8">
                      <button type="button" onClick={reset} className="btn-ghost">
                        Submit another RFQ
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {step === 1 && (
                      <div className="space-y-5">
                        <Field label="Company Name">
                          <input
                            className="input"
                            value={data.company_name}
                            onChange={(e) => set("company_name", e.target.value)}
                            placeholder="Acme Industries Pvt. Ltd."
                          />
                        </Field>
                        <Field label="Country">
                          <select
                            className="select"
                            value={data.country}
                            onChange={(e) => set("country", e.target.value)}
                          >
                            <option value="">Select country…</option>
                            {COUNTRIES.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </Field>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <Field label="Target Industry">
                            <select
                              className="select"
                              value={data.industry}
                              onChange={(e) => set("industry", e.target.value)}
                            >
                              <option value="">Select…</option>
                              {INDUSTRIES.map((c) => (
                                <option key={c}>{c}</option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Project Timeline">
                            <select
                              className="select"
                              value={data.timeline}
                              onChange={(e) => set("timeline", e.target.value)}
                            >
                              <option value="">Select…</option>
                              {TIMELINES.map((c) => (
                                <option key={c}>{c}</option>
                              ))}
                            </select>
                          </Field>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-6">
                        <Field label="Material Required">
                          <select
                            className="select"
                            value={data.material}
                            onChange={(e) => set("material", e.target.value)}
                          >
                            <option value="">Select material…</option>
                            {MATERIALS.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Manufacturing Process (multi-select)">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                            {PROCESSES.map((p) => (
                              <button
                                type="button"
                                key={p}
                                onClick={() => toggleProcess(p)}
                                className={`checkbox-card ${data.processes.includes(p) ? "active" : ""}`}
                              >
                                <span
                                  className="w-4 h-4 shrink-0 rounded border"
                                  style={
                                    data.processes.includes(p)
                                      ? { ...tbr.accent, ...tb.accent }
                                      : { borderColor: "var(--color-border)" }
                                  }
                                />
                                {p}
                              </button>
                            ))}
                          </div>
                        </Field>
                        <Field label="Annual Volume">
                          <select
                            className="select"
                            value={data.annual_volume}
                            onChange={(e) => set("annual_volume", e.target.value)}
                          >
                            <option value="">Select volume…</option>
                            {VOLUMES.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Technical Notes">
                          <textarea
                            className="textarea min-h-[100px]"
                            value={data.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            placeholder="Tolerance critical features, finish requirements…"
                          />
                        </Field>

                        <Field label="CAD File Upload">
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDrag(true);
                            }}
                            onDragLeave={() => setDrag(false)}
                            onDrop={onDrop}
                            onClick={() => inputRef.current?.click()}
                            className="cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors duration-300 bg-[#07071a]"
                            style={
                              drag
                                ? { borderColor: "var(--color-accent)", background: "rgba(37,99,235,0.1)" }
                                : { borderColor: "var(--color-border)" }
                            }
                          >
                            <Upload size={28} className="mx-auto mb-3" style={tc.accent} />
                            <div className="text-[15px] font-medium" style={tc.primary}>
                              Drag & drop CAD files or{" "}
                              <span style={tc.accent}>browse</span>
                            </div>
                            <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                              {ACCEPTED_EXT.map((e) => (
                                <span
                                  key={e}
                                  className="text-[10px] px-2 py-1 rounded-full border"
                                  style={{ ...tbr.default, ...tc.muted }}
                                >
                                  {e}
                                </span>
                              ))}
                            </div>
                            <p className="mt-4 text-[13px]" style={tc.muted}>
                              Please email CAD files to rfq@malunjkar.com
                            </p>
                            <input
                              ref={inputRef}
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) =>
                                setFiles((p) => [...p, ...Array.from(e.target.files || [])])
                              }
                            />
                          </div>
                        </Field>

                        {files.length > 0 && (
                          <div className="space-y-2">
                            {files.map((f, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between px-4 py-3 rounded-lg border bg-[#07071a]"
                                style={tbr.default}
                              >
                                <div className="truncate text-[14px]" style={tc.primary}>
                                  {f.name}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFile(i)}
                                  className="ml-2"
                                  style={tc.muted}
                                  aria-label={`Remove file ${f.name}`}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-6">
                        <div className="rounded-xl border bg-[#07071a] p-6" style={tbr.default}>
                          <div className="label-cap mb-4" style={tc.accent}>Submission Summary</div>
                          <ReviewRow label="Company" value={data.company_name} />
                          <ReviewRow label="Country" value={data.country} />
                          <ReviewRow label="Industry" value={data.industry} />
                          <ReviewRow label="Timeline" value={data.timeline} />
                          <ReviewRow label="Material" value={data.material} />
                          <ReviewRow label="Processes" value={data.processes.join(", ")} />
                          <ReviewRow label="Volume" value={data.annual_volume} />
                          {data.notes && <ReviewRow label="Notes" value={data.notes} />}
                          <ReviewRow
                            label="Files"
                            value={files.length ? `${files.length} file(s) attached` : "None"}
                          />
                        </div>

                        <label
                          className="flex items-start gap-3 p-4 rounded-xl border bg-[#07071a] cursor-pointer hover:border-[#2a3a5c] transition-colors"
                          style={tbr.default}
                        >
                          <input
                            type="checkbox"
                            checked={data.nda_requested}
                            onChange={(e) => set("nda_requested", e.target.checked)}
                            className="mt-1"
                            style={{ accentColor: "var(--color-accent)" }}
                          />
                          <span className="text-[14px]" style={tc.primary}>
                            Request Mutual NDA Before File Processing
                            <span className="block text-[12px] mt-1" style={tc.muted}>
                              We will share a mutual NDA template before any technical review
                              begins.
                            </span>
                          </span>
                        </label>

                        {error && <div className="text-[13px]" style={tc.danger}>{error}</div>}
                      </div>
                    )}

                    <div className="mt-8 flex items-center justify-between">
                      <button
                        type="button"
                        disabled={step === 1}
                        onClick={() => setStep((s) => Math.max(1, s - 1))}
                        className="btn-ghost"
                      >
                        <ArrowLeft size={16} /> Back
                      </button>
                      {step < 3 ? (
                        <button
                          type="button"
                          disabled={(step === 1 && !validStep1) || (step === 2 && !validStep2)}
                          onClick={() => setStep((s) => s + 1)}
                          className="btn-primary"
                        >
                          Continue <ArrowRight size={16} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={submit}
                          className="btn-primary"
                        >
                          {submitting ? "Submitting…" : "Submit Secure RFQ"}{" "}
                          <ArrowRight size={16} />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-cap mb-2" style={tc.muted}>{label}</div>
      {children}
    </label>
  );
}

function ReviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="review-row">
      <div className="label-cap" style={tc.muted}>{label}</div>
      <div className="col-span-2 text-[14px]" style={tc.primary}>{value}</div>
    </div>
  );
}
